import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, Users, AlertCircle, Loader2, Share2, Flame, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, AppNotification } from '../../services/notificationService';
import { createChallenge, ChallengeType } from '../../services/challengeService';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import html2canvas from 'html2canvas';
import { getAvatarUrl } from '../../utils/avatarUtils';

export default function NotificationCenter() {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen && 'clearAppBadge' in navigator) {
      (navigator as any).clearAppBadge().catch((error: any) => {
        console.error('Failed to clear app badge:', error);
      });
    }
  }, [isOpen]);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [inactiveFriendId, setInactiveFriendId] = useState<string | null>(null);
  const [inactiveFriendName, setInactiveFriendName] = useState<string | null>(null);
  const [inactiveDays, setInactiveDays] = useState<number | null>(null);
  const [isProcessingChallenge, setIsProcessingChallenge] = useState<string | null>(null);
  const [challengeSuccess, setChallengeSuccess] = useState<string | null>(null);
  
  const [sharingNotification, setSharingNotification] = useState<AppNotification | null>(null);
  const receivedProvokeShareRef = useRef<HTMLDivElement>(null);
  const inactivityShareRef = useRef<HTMLDivElement>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = getNotifications(currentUser.uid, (notifs) => {
      setNotifications(notifs);
    });
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleNotificationClick = async (notification: AppNotification) => {
    if (currentUser && !notification.read && notification.id) {
      await markNotificationAsRead(currentUser.uid, notification.id);
    }
    setIsOpen(false);
    
    if (notification.type === 'session_invite' && notification.metadata?.sessionId) {
      navigate(`/session/${notification.metadata.sessionId}`);
    } else if (notification.type === 'stop_game_invite' && notification.metadata?.roomCode) {
      navigate(`/stop-game?room=${notification.metadata.roomCode}`);
    } else if (notification.type === 'inactivity_alert' && notification.fromUserId) {
      setInactiveFriendId(notification.fromUserId);
      setInactiveFriendName(notification.fromUserName || 'your friend');
      setInactiveDays(notification.metadata?.daysMissed || null);
      setShowChallengeModal(true);
    } else if (notification.type === 'friend_request') {
      navigate('/social'); // They can accept it there
    } else {
      navigate('/social');
    }
  };

  const handleCreateChallenge = async (type: ChallengeType) => {
    if (currentUser && inactiveFriendId) {
      setIsProcessingChallenge(type);
      try {
        await createChallenge(currentUser.uid, inactiveFriendId, type);
        setChallengeSuccess(type);
        setTimeout(() => {
          setShowChallengeModal(false);
          setInactiveFriendId(null);
          setIsProcessingChallenge(null);
          setChallengeSuccess(null);
          navigate('/social', { state: { activeTab: 'challenges' } });
        }, 1500);
      } catch (error) {
        console.error("Failed to create challenge", error);
        setIsProcessingChallenge(null);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!currentUser) return;
    const unreadIds = notifications.filter(n => !n.read && n.id).map(n => n.id!);
    if (unreadIds.length > 0) {
      await markAllNotificationsAsRead(currentUser.uid, unreadIds);
    }
  };

  const handleMarkAsRead = async (e: React.MouseEvent, notification: AppNotification) => {
    e.stopPropagation();
    if (currentUser && notification.id && !notification.read) {
      await markNotificationAsRead(currentUser.uid, notification.id);
    }
  };

  const handleShareInactivity = async () => {
    if (!inactivityShareRef.current) return;
    
    try {
      const canvas = await html2canvas(inactivityShareRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null
      });
      
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], 'inactivity-alert.png', { type: 'image/png' });
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              title: 'Wake up!',
              text: `${inactiveFriendName} has been inactive on LinguaLearn! Time for some social bullying. 😈`,
              files: [file]
            });
          } catch (err) {
            console.error('Error sharing', err);
          }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'inactivity-alert.png';
          a.click();
          URL.revokeObjectURL(url);
        }
      });
    } catch (error) {
      console.error('Error generating image', error);
    }
  };

  const handleShareNotification = async (notif: AppNotification) => {
    setSharingNotification(notif);
    // Wait for state to update
    setTimeout(async () => {
      if (!receivedProvokeShareRef.current) return;
      try {
        const canvas = await html2canvas(receivedProvokeShareRef.current, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: null
        });
        
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const file = new File([blob], 'provocation.png', { type: 'image/png' });
          if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                title: 'I was challenged!',
                text: `${notif.fromUserName} provoked me on LinguaLearn! Watch me beat them. 🔥`,
                files: [file]
              });
            } catch (err) {
              console.error('Error sharing', err);
            }
          } else {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'provocation.png';
            a.click();
            URL.revokeObjectURL(url);
          }
        });
      } catch (error) {
        console.error('Error generating image', error);
      }
    }, 100);
  };

  if (!currentUser) return null;

  return (
    <div className="relative mr-2 md:mr-4" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] md:min-w-[22px] md:h-[22px] px-1 text-[10px] md:text-xs font-bold text-white bg-red-500 rounded-full border-2 border-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed left-4 right-4 top-16 md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 w-auto md:w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden md:transform md:origin-top-right transition-all">
          <div className="p-3 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
            <h3 className="font-semibold text-gray-800 flex items-center">
              <Bell className="w-4 h-4 mr-2 text-indigo-500" /> Notifications
            </h3>
            {unreadCount > 0 && (
              <button 
                onClick={handleMarkAllAsRead}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center transition-colors px-2 py-1 rounded-md hover:bg-indigo-50"
              >
                <Check className="w-3 h-3 mr-1" /> Mark all read
              </button>
            )}
          </div>
          
          <div className="max-h-[350px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500 flex flex-col items-center">
                <Users className="w-8 h-8 mb-2 text-gray-300" />
                <p className="text-sm">No notifications yet.</p>
                <p className="text-xs mt-1">Connect with friends to see their activity!</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div 
                  key={notif.id} 
                  onClick={() => handleNotificationClick(notif)}
                  className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors flex gap-3 ${!notif.read ? 'bg-indigo-50/30' : ''}`}
                >
                  <div className="flex-shrink-0 relative mt-1">
                    {notif.fromUserPhoto ? (
                      <img referrerPolicy="no-referrer" src={notif.fromUserPhoto} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-200" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-500 font-bold text-sm">
                        {notif.fromUserName?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                    {!notif.read && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-indigo-500 rounded-full border-2 border-white"></span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notif.read ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                      {notif.message}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      {new Date(notif.timestamp).toLocaleDateString('en-GB')} at {new Date(notif.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </p>
                  </div>
                  {!notif.read && (
                    <button 
                      onClick={(e) => handleMarkAsRead(e, notif)}
                      className="flex-shrink-0 p-1 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 rounded-full transition-colors h-fit self-center"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  {notif.type === 'provocation' && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleShareNotification(notif); }}
                      className="flex-shrink-0 p-1 text-pink-400 hover:text-pink-600 hover:bg-pink-100 rounded-full transition-colors h-fit self-center ml-1"
                      title="Share Provocation"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
          
          <div className="p-2 border-t border-gray-100 bg-gray-50 text-center">
            <button 
              onClick={() => { setIsOpen(false); navigate('/social'); }}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors w-full py-1"
            >
              View all friends
            </button>
          </div>
        </div>
      )}

      <Modal isOpen={showChallengeModal} onClose={() => setShowChallengeModal(false)} title="Provoke & Challenge! ⚔️">
        <div className="p-4">
          <p className="text-gray-600 mb-6 text-center">
            {inactiveFriendName} has been inactive {inactiveDays ? `for ${inactiveDays} days!` : 'lately!'} Wake them up by challenging them to a 24-hour race. Which activity do you want to challenge them to?
          </p>
          <div className="grid grid-cols-1 gap-3">
            <button 
              onClick={() => handleCreateChallenge('points')} 
              disabled={!!isProcessingChallenge}
              className="bg-indigo-50 border border-indigo-200 text-indigo-700 py-3 rounded-xl font-medium hover:bg-indigo-100 transition-colors flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isProcessingChallenge === 'points' && !challengeSuccess ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              {challengeSuccess === 'points' ? <Check className="w-5 h-5 text-green-600" /> : null}
              {challengeSuccess === 'points' ? 'Challenge Sent!' : 'Total Points Race'}
            </button>
            <button 
              onClick={() => handleCreateChallenge('crime_game')} 
              disabled={!!isProcessingChallenge}
              className="bg-purple-50 border border-purple-200 text-purple-700 py-3 rounded-xl font-medium hover:bg-purple-100 transition-colors flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isProcessingChallenge === 'crime_game' && !challengeSuccess ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              {challengeSuccess === 'crime_game' ? <Check className="w-5 h-5 text-green-600" /> : null}
              {challengeSuccess === 'crime_game' ? 'Challenge Sent!' : 'Crime Game Solve'}
            </button>
            <button 
              onClick={() => handleCreateChallenge('daily_training')} 
              disabled={!!isProcessingChallenge}
              className="bg-emerald-50 border border-emerald-200 text-emerald-700 py-3 rounded-xl font-medium hover:bg-emerald-100 transition-colors flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isProcessingChallenge === 'daily_training' && !challengeSuccess ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              {challengeSuccess === 'daily_training' ? <Check className="w-5 h-5 text-green-600" /> : null}
              {challengeSuccess === 'daily_training' ? 'Challenge Sent!' : 'Daily Training'}
            </button>
            <button 
              onClick={() => handleCreateChallenge('word_of_day')} 
              disabled={!!isProcessingChallenge}
              className="bg-amber-50 border border-amber-200 text-amber-700 py-3 rounded-xl font-medium hover:bg-amber-100 transition-colors flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isProcessingChallenge === 'word_of_day' && !challengeSuccess ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              {challengeSuccess === 'word_of_day' ? <Check className="w-5 h-5 text-green-600" /> : null}
              {challengeSuccess === 'word_of_day' ? 'Challenge Sent!' : 'Word of the Day'}
            </button>
            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">Or</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>
            <button 
              disabled={!!isProcessingChallenge}
              onClick={() => {
                setShowChallengeModal(false);
                navigate('/social', { state: { provokeUserId: inactiveFriendId } });
                setInactiveFriendId(null);
              }} 
              className="bg-red-50 border border-red-200 text-red-700 py-3 rounded-xl font-medium hover:bg-red-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <AlertCircle className="w-5 h-5" />
              Just Send a Provocation 🔥
            </button>
            <button 
              disabled={!!isProcessingChallenge}
              onClick={handleShareInactivity}
              className="bg-pink-50 border border-pink-200 text-pink-700 py-3 rounded-xl font-medium hover:bg-pink-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <Share2 className="w-5 h-5" />
              Share (Social Bullying 😈)
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Hidden Share Card for Received Provocation */}
      <div className="absolute -left-[9999px] top-0">
        <div ref={receivedProvokeShareRef} className="w-[600px] h-[400px] bg-gradient-to-br from-red-600 to-rose-700 flex flex-col items-center justify-center p-8 font-sans text-white">
          <div className="flex items-center gap-3 mb-6">
            <Flame className="w-12 h-12 text-yellow-300" />
            <h1 className="text-4xl font-black tracking-tight uppercase">I've been challenged!</h1>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 w-full max-w-lg border border-white/20 shadow-2xl flex flex-col items-center relative">
             <div className="absolute -top-10 flex gap-4">
                <div className="w-20 h-20 rounded-full border-4 border-white shadow-xl overflow-hidden bg-white">
                   <img referrerPolicy="no-referrer" src={sharingNotification?.fromUserPhoto || getAvatarUrl(sharingNotification?.fromUserName || sharingNotification?.fromUserId || 'User')} className="w-full h-full object-cover" crossOrigin="anonymous" />
                </div>
                <div className="w-20 h-20 rounded-full border-4 border-red-200 shadow-xl overflow-hidden bg-white">
                   <img referrerPolicy="no-referrer" src={currentUser?.photoURL || getAvatarUrl(currentUser?.uid)} className="w-full h-full object-cover opacity-70 sepia" crossOrigin="anonymous" />
                </div>
             </div>
             
             <div className="mt-12 text-center">
               <p className="text-2xl font-bold italic mb-4 leading-relaxed">"{sharingNotification?.message || 'Wake up and study!'}"</p>
               <p className="text-sm text-red-200 font-medium uppercase tracking-widest">— {sharingNotification?.fromUserName} poking me</p>
             </div>
          </div>
          
          <div className="mt-8 flex flex-col items-center text-center">
             <p className="text-white/80 font-bold tracking-widest uppercase text-xs mb-1">Watch me beat them</p>
             <p className="text-white font-bold text-sm bg-black/20 px-4 py-1 rounded-full backdrop-blur-sm">https://lingualearn.web.app/</p>
          </div>
        </div>

        {/* Hidden Share Card for Inactivity Alert (Social Bullying) */}
        <div ref={inactivityShareRef} className="w-[600px] h-[400px] bg-gradient-to-br from-indigo-800 to-slate-900 flex flex-col items-center justify-center p-8 font-sans text-white mt-8">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-12 h-12 text-orange-400" />
            <h1 className="text-4xl font-black tracking-tight uppercase">Missing in Action!</h1>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 w-full max-w-lg border border-white/20 shadow-2xl flex flex-col items-center relative">
             <div className="absolute -top-10">
                <div className="w-24 h-24 rounded-full border-4 border-orange-400 shadow-xl overflow-hidden bg-white">
                   <img referrerPolicy="no-referrer" src={getAvatarUrl(inactiveFriendId || 'User')} className="w-full h-full object-cover grayscale opacity-80" crossOrigin="anonymous" />
                </div>
             </div>
             
             <div className="mt-14 text-center">
               <p className="text-3xl font-black mb-2 text-white">{inactiveFriendName}</p>
               <p className="text-xl font-bold text-orange-300">Hasn't studied in {inactiveDays || 'several'} days!</p>
               <p className="text-sm text-gray-300 mt-4 italic">Time to do some social bullying to get them back on track.</p>
             </div>
          </div>
          
          <div className="mt-8 flex flex-col items-center text-center">
             <p className="text-white/80 font-bold tracking-widest uppercase text-xs mb-1">Help me annoy them</p>
             <p className="text-white font-bold text-sm bg-black/30 px-4 py-1 rounded-full backdrop-blur-sm">https://lingualearn.web.app/</p>
          </div>
        </div>
      </div>
    </div>
  );
}
